import { useParams, useNavigate } from 'react-router-dom'
import { useContext, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { APIContext } from 'context/APIContext'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import { UsersIcon, CalendarDaysIcon, IdentificationIcon, EnvelopeIcon, UserMinusIcon } from '@heroicons/react/24/outline'
import { equipmentMedia } from 'functions/equipmentUtils'
import bandaidIcon from 'media/icons/bandaid.png'
import { GetAthleteInviteToSignUpWithCoachPlainText } from 'functions/textUtils'
// import trainingPeaksAppIcon from 'media/integrations/training_peaks/TrainingPeaks_App_Icon.jpg'

import ScreenFrame from "components/Frame/ScreenFrame"
import KinesisProfileBreadCrumbs from 'components/Profile/KinesisProfileBreadCrumbs'
import ContentFrame from "components/Frame/ContentFrame"

export default function KinesisProfileSettings({ propProfileType = 'individual' }) {

    const { urlGroupId, urlAthleteId } = useParams()
    const navigate = useNavigate()

    const { GetKinesisUserGroupDetails, GetUserDetails } = useContext(APIContext)
    const { ShowModal } = useContext(ModalContext)

    const organizationName = useSelector(state => state.user.organizationName)
    const coachFirstName = useSelector(state => state.user.firstName)
    const coachLastName = useSelector(state => state.user.lastName)

    const [kinesisProfileDetails, setKinesisProfileDetails] = useState(null)

    const userEmail = propProfileType === 'group' ? null : kinesisProfileDetails?.email
    const coachInviteSignUpCode = propProfileType === 'group' ? null : kinesisProfileDetails?.coachInviteSignUpCode

    const LoadKinesisProfileDetails = async () => {

        setKinesisProfileDetails(null)
        
        var newKinesisProfileDetails = null

        if (propProfileType === 'group') {
            newKinesisProfileDetails = await GetKinesisUserGroupDetails(urlGroupId)
        }

        if (propProfileType === 'individual') {
            newKinesisProfileDetails = await GetUserDetails(urlAthleteId)
        }

        setKinesisProfileDetails(newKinesisProfileDetails)
    }

    const ShowInviteAthleteEmailPreviewModal = () => {
        const modalData = {
            key: ModalKey.PlainTextEmailPreview,
            size: 'half',
            titleText: 'Invite Email Preview',
            bodyText: [
                'This is a preview of the plain text email that will be sent to the athletes.',
                'Click anywhere below to copy the text to your clipboard.'
            ],
            plainText: GetAthleteInviteToSignUpWithCoachPlainText(coachFirstName, coachLastName, organizationName, coachInviteSignUpCode),
            includeSendFunctionality: true
        }

        ShowModal(modalData)
    }

    const ShowOffboardAthleteWarningModal = () => {
        const modalData = {
            key: ModalKey.ActionWarning,
            size: 'quarter',
            titleText: 'Offboard Athlete?',
            bodyText: [
                'Are you sure you want to offboard this athlete?',
                'You will no longer be able to view this athlete\'s data and they will no longer be able to complete workouts.',
                'Offboards can be done on the billing page.'
            ],
            buttonText: 'Go To Billing Page',
            buttonAction: () => {
                navigate('/settings/billing')
            }
        }

        ShowModal(modalData)
    }

    useEffect(() => { LoadKinesisProfileDetails() }, [propProfileType, urlGroupId, urlAthleteId])

    if (kinesisProfileDetails === null) {
        return (
            <ScreenFrame>
                <div className='max-w-2xl mx-auto space-y-4'>
                    <div className='flex-shrink-0 h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='flex-shrink-0 h-[400px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    const profileName = propProfileType === 'group' ? kinesisProfileDetails?.name : `${kinesisProfileDetails?.firstName} ${kinesisProfileDetails?.lastName}`

    const groupSettingsOptions = [
        {
            visible: true,
            icon: <UsersIcon className="h-6 w-6 m-1 text-gray-600" />,
            title: 'Athletes',
            description: 'Manage athletes in this group.',
            action: () => { navigate(`/group/${urlGroupId}/athletes`, { state: { profileName } }) }
        },
        {
            visible: true,
            icon: <CalendarDaysIcon className="h-6 w-6 m-1 text-gray-600" />,
            title: 'Schedule',
            description: 'Set the default weekly workout schedule',
            action: () => { navigate(`/group/${urlGroupId}/schedule`, { state: { profileName } }) }
        },
        {
            visible: true,
            icon: <IdentificationIcon className="h-6 w-6 m-1 text-gray-600" />,
            title: 'Fitness Profile',
            description: 'Update group fitness information',
            action: () => { navigate(`/group/${urlGroupId}/fitnessProfile`, { state: { profileName } }) }
        },
        {
            visible: true,
            icon: <img src={equipmentMedia['band'].imageURL} className="h-8 w-8" />,
            title: 'Equipment',
            description: 'Update group equipment preferences',
            action: () => { navigate(`/group/${urlGroupId}/equipment`, { state: { profileName } }) }
        }
        // {
        //     visible: true,
        //     title: 'Workouts',
        //     description: 'View completed workouts for this group.',
        //     route: '#'
        // }
    ]

    const individualFitnessSettings = [
        {
            visible: true,
            icon: <CalendarDaysIcon className="h-6 w-6 m-1 text-gray-600" />,
            title: 'Schedule',
            description: 'Set the default weekly workout schedule',
            action: () => { navigate(`/athlete/${urlAthleteId}/schedule`, { state: { profileName } }) }
        },
        {
            visible: true,
            icon: <IdentificationIcon className="h-6 w-6 m-1 text-gray-600" />,
            title: 'Fitness Profile',
            description: 'Edit athlete fitness information',
            action: () => { navigate(`/athlete/${urlAthleteId}/fitnessProfile`, { state: { profileName } }) }
        },
        {
            visible: true,
            icon: <img src={equipmentMedia['band'].imageURL} className="h-8 w-8" />,
            title: 'Equipment',
            description: 'Update athlete equipment preferences',
            action: () => { navigate(`/athlete/${urlAthleteId}/equipment`, { state: { profileName } }) }
        },
        {
            visible: true,
            icon: <img src={bandaidIcon} className="h-7 w-7 m-0.5 invert opacity-50" />,
            title: 'Injuries',
            description: 'Update athlete injuries',
            action: () => { navigate(`/athlete/${urlAthleteId}/injuries`, { state: { profileName } }) }
        },
        // {
        //     title: 'Assessments',
        //     description: 'View the assessments for this athlete',
        //     route: `#`
        // },
        // {
        //     title: 'Workouts',
        //     description: 'View the workouts for this athlete',
        //     route: `#`
        // }
    ]

    const individualMiscSettings = [
        {
            visible: (userEmail === null || userEmail === undefined || userEmail.length === 0),
            icon: <EnvelopeIcon className="h-6 w-6 m-1 text-gray-600" />,
            title: 'Invite Athlete To Account',
            description: 'Send an invite code over email for an athlete to use during sign up',
            action: ShowInviteAthleteEmailPreviewModal
        },
        // {
        //     icon: <img src={trainingPeaksAppIcon} className="h-7 w-7 m-0.5 rounded-md" />,
        //     title: 'TrainingPeaks Integration',
        //     description: 'Manage the TrainingPeaks connection for this athlete',
        //     action: () => { navigate(`/onboarding/athletes/trainingPeaks?defaultValue=no-mapping`) }
        // },
        {
            visible: true,
            icon: <UserMinusIcon className="h-6 w-6 m-1 text-gray-600" />,
            title: 'Offboard Athlete',
            description: 'Remove this athlete from your organization',
            action: ShowOffboardAthleteWarningModal
        }
    ]

    const fitnessSettings = propProfileType === 'group' ? groupSettingsOptions : individualFitnessSettings
    const miscSettings = propProfileType === 'group' ? [] : individualMiscSettings

    return (
        <ScreenFrame>
            <div className='max-w-2xl mx-auto space-y-4'>
                <KinesisProfileBreadCrumbs 
                    propProfileType={propProfileType} 
                    profileName={profileName} 
                />

                <div className='flex items-center space-y-1 gap-x-2 pb-1'>
                    {propProfileType === 'group' && (
                        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                            {kinesisProfileDetails?.name ?? 'Group'} Settings
                        </h1>
                    )}

                    {propProfileType === 'individual' && (
                        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                            {kinesisProfileDetails?.firstName + ' ' + kinesisProfileDetails?.lastName ?? 'User'} Settings
                        </h1>
                    )}
                </div>

                <ContentFrame className='divide-y divide-gray-200'>
                    {fitnessSettings
                    .filter((setting) => setting.visible)
                    .map((setting, index) => (
                        <div 
                            key={index}
                            className="w-full flex items-center px-6 py-4 gap-x-6 cursor-pointer hover:bg-gray-50 transition-all"
                            onClick={setting.action}
                        >
                            {setting.icon}
                            
                            <div className='flex-1'>
                                <div className="text-lg text-gray-800 flex-1">{setting.title}</div>
                                <div className="text-sm text-gray-500">{setting.description}</div>
                            </div>

                            <ChevronRightIcon className="h-6 w-6 text-gray-400 group-hover:text-orange-500 transition-colors" />
                        </div>
                    ))}
                </ContentFrame>

                {miscSettings.length > 0 && (
                    <ContentFrame className='divide-y divide-gray-200'>
                        {miscSettings
                        .filter((setting) => setting.visible)
                        .map((setting, index) => (
                            <div 
                                key={index}
                                className="w-full flex items-center px-6 py-4 gap-x-6 cursor-pointer hover:bg-gray-50 transition-all"
                                onClick={setting.action}
                            >
                                {setting.icon}

                                <div className='flex-1'>
                                    <div className="text-lg text-gray-800 flex-1">{setting.title}</div>
                                    <div className="text-sm text-gray-500">{setting.description}</div>
                                </div>

                                <ChevronRightIcon className="h-6 w-6 text-gray-400 group-hover:text-orange-500 transition-colors" />
                            </div>
                        ))}
                    </ContentFrame>
                )}
            </div>
        </ScreenFrame>
    )
}